import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, AlertTriangle, Scale, HelpCircle } from "lucide-react";

import AppErrorState from "@/components/ui/AppErrorState";
import { Button } from "@/components/ui/button";
import { getApiErrorMessage } from "@/lib/api";
import { getFinancialReconciliation } from "@/lib/pilotageApi";

const MONTH_OPTIONS = [
  { value: "", label: "Toute l'année (12 mois)" },
  { value: 1, label: "Janvier" },
  { value: 2, label: "Février" },
  { value: 3, label: "Mars" },
  { value: 4, label: "Avril" },
  { value: 5, label: "Mai" },
  { value: 6, label: "Juin" },
  { value: 7, label: "Juillet" },
  { value: 8, label: "Août" },
  { value: 9, label: "Septembre" },
  { value: 10, label: "Octobre" },
  { value: 11, label: "Novembre" },
  { value: 12, label: "Décembre" },
];

const formatCurrency = (val) => {
  if (val === null || val === undefined || isNaN(val)) return "0,00 €";
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(val);
};

const formatDelta = (val) => {
  if (val === null || val === undefined || isNaN(val) || Math.abs(val) < 0.01) {
    return "0,00 €";
  }
  const formatted = formatCurrency(Math.abs(val));
  return val > 0 ? `+${formatted}` : `-${formatted}`;
};

export default function FinancialReconciliationPage() {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(() => {
    const requested = Number(new URLSearchParams(window.location.search).get("year"));
    return [2026, 2027].includes(requested) ? requested : 2026;
  });
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get("month");
    return requested ? Number(requested) : "";
  });
  const [activeTab, setActiveTab] = useState("monthly"); // "monthly" | "collaborators"

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");
      const params = { year: selectedYear };
      if (selectedMonth) params.month = selectedMonth;
      const res = await getFinancialReconciliation(params);
      setData(res);
    } catch (err) {
      setError(getApiErrorMessage(err, "Impossible de charger la réconciliation financière."));
    } finally {

      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedYear, selectedMonth]);

  const annualA = data?.annual_vision_a;
  const annualB = data?.annual_vision_b;
  const annualVar = data?.annual_variance;

  return (
    <div className="space-y-6 p-6">
      {/* En-tête et retour */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Link
              to={`/admin/pilotage/dashboard?year=${selectedYear}`}
              className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-800 transition"
            >
              <ArrowLeft className="w-4 h-4" />
              Pilotage
            </Link>
            <span className="text-slate-300">/</span>
            <span className="text-sm font-semibold text-slate-700">Rapprochement financier</span>
          </div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
              <Scale className="w-6 h-6 text-indigo-600" />
              Rapprochement Financier
            </h1>
            <span className="bg-amber-100 text-amber-800 text-xs font-semibold px-2.5 py-0.5 rounded-full border border-amber-200">
              Double Calcul Consultatif (ADMIN)
            </span>
          </div>
        </div>

        {/* Sélecteurs Année et Mois */}
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
            {[2026, 2027].map((yr) => (
              <button
                key={yr}
                onClick={() => setSelectedYear(yr)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition ${
                  selectedYear === yr
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {yr}
              </button>
            ))}
          </div>

          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value ? Number(e.target.value) : "")}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none"
          >
            {MONTH_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Bannière de Disclaimer officiel */}
      <div className="rounded-xl border border-blue-200 bg-blue-50/70 p-4 text-sm text-blue-900 flex items-start gap-3 shadow-xs">
        <HelpCircle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">Cadre d'analyse consultative (Étape 5)</p>
          <p className="text-blue-800/90 text-xs mt-0.5">
            Ce tableau présente en miroir la <strong>Vision Actuelle</strong> (moteur de missions historique) et la{" "}
            <strong>Vision Enrichie</strong> (intégration des charges salariales permanentes des CDI, CDD et alternants).
            Les chiffres enrichis sont consultatifs et <em>n'altèrent en rien le P&L comptable officiel de production</em>.
          </p>
        </div>
      </div>

      {/* Gestion Erreur & Chargement */}
      {error && <AppErrorState message={error} onRetry={fetchData} />}

      {loading && !error && (
        <div className="p-12 text-center text-slate-400 animate-pulse text-sm">
          Calcul du rapprochement financier en cours...
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* Cartes KPI Synthétiques en Double Vision */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* 1. CA Net (Invariant) */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs">
              <div className="flex items-center justify-between text-xs font-medium text-slate-500">
                <span>Chiffre d'Affaires Net</span>
                <span className="text-emerald-600 font-semibold flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Invariant
                </span>
              </div>
              <div className="mt-2 text-xl font-bold text-slate-900">
                {formatCurrency(annualA?.ca_net)}
              </div>
              <p className="mt-1 text-xs text-slate-500">Identique sur les deux visions</p>
            </div>

            {/* 2. Coûts Totaux */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs">
              <div className="text-xs font-medium text-slate-500">Coûts Consultants & Prestations</div>
              <div className="mt-2 flex items-baseline justify-between">
                <div>
                  <span className="text-xs text-slate-400 block">Actuel :</span>
                  <span className="text-base font-semibold text-slate-700">{formatCurrency(annualA?.total_consultant_cost)}</span>
                </div>
                <div className="text-right">
                  <span className="text-xs text-indigo-600 font-medium block">Enrichi :</span>
                  <span className="text-lg font-bold text-slate-900">{formatCurrency(annualB?.total_consultant_cost)}</span>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between text-xs">
                <span className="text-slate-500">Écart Coûts :</span>
                <span className={`font-semibold ${annualVar?.total_cost_delta > 0 ? "text-amber-600" : "text-slate-700"}`}>
                  {formatDelta(annualVar?.total_cost_delta)}
                </span>
              </div>
            </div>

            {/* 3. Marge Brute */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs">
              <div className="text-xs font-medium text-slate-500">Marge Brute Globale</div>
              <div className="mt-2 flex items-baseline justify-between">
                <div>
                  <span className="text-xs text-slate-400 block">Actuelle :</span>
                  <span className="text-base font-semibold text-slate-700">{formatCurrency(annualA?.gross_margin)}</span>
                </div>
                <div className="text-right">
                  <span className="text-xs text-indigo-600 font-medium block">Enrichie :</span>
                  <span className="text-lg font-bold text-slate-900">{formatCurrency(annualB?.gross_margin)}</span>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between text-xs">
                <span className="text-slate-500">Taux Enrichi :</span>
                <span className="font-semibold text-slate-800">{annualB?.margin_pct}%</span>
              </div>
            </div>

            {/* 4. Résultat Net */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs">
              <div className="text-xs font-medium text-slate-500">Résultat d'Exploitation</div>
              <div className="mt-2 flex items-baseline justify-between">
                <div>
                  <span className="text-xs text-slate-400 block">Actuel :</span>
                  <span className="text-base font-semibold text-slate-700">{formatCurrency(annualA?.net_result)}</span>
                </div>
                <div className="text-right">
                  <span className="text-xs text-indigo-600 font-medium block">Enrichi :</span>
                  <span className={`text-lg font-bold ${annualB?.net_result >= 0 ? "text-slate-900" : "text-rose-600"}`}>
                    {formatCurrency(annualB?.net_result)}
                  </span>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between text-xs">
                <span className="text-slate-500">Écart Résultat :</span>
                <span className="font-semibold text-slate-800">{formatDelta(annualVar?.net_result_delta)}</span>
              </div>
            </div>
          </div>

          {/* Onglets de Navigation Vue Mensuelle / Vue Collaborateurs */}
          <div className="border-b border-slate-200 flex gap-4">
            <button
              onClick={() => setActiveTab("monthly")}
              className={`pb-3 text-sm font-semibold border-b-2 transition ${
                activeTab === "monthly"
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              Échéancier Mensuel (Double Colonne)
            </button>
            <button
              onClick={() => setActiveTab("collaborators")}
              className={`pb-3 text-sm font-semibold border-b-2 transition ${
                activeTab === "collaborators"
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              Détail par Collaborateur ({data.collaborator_rows?.length || 0})
            </button>
          </div>

          {/* TAB 1 : Échéancier Mensuel */}
          {activeTab === "monthly" && (
            <div className="rounded-xl border border-slate-200 bg-white shadow-xs overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-slate-50/80 border-b border-slate-200 text-slate-500 font-semibold uppercase tracking-wider">
                      <th className="py-3 px-4">Période</th>
                      <th className="py-3 px-4">CA Net</th>
                      <th className="py-3 px-4 text-slate-600 bg-slate-100/50">Coûts Actuel</th>
                      <th className="py-3 px-4 text-indigo-900 bg-indigo-50/50">Coûts Enrichi</th>
                      <th className="py-3 px-4">Écart Coûts</th>
                      <th className="py-3 px-4 text-slate-600 bg-slate-100/50">Marge Actuelle</th>
                      <th className="py-3 px-4 text-indigo-900 bg-indigo-50/50">Marge Enrichie</th>
                      <th className="py-3 px-4">Écart Marge</th>
                      <th className="py-3 px-4">Explication de l'Écart</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.monthly_rows.map((row) => (
                      <tr key={row.key} className="hover:bg-slate-50/50 transition">
                        <td className="py-3 px-4 font-semibold text-slate-800">{row.month}</td>
                        <td className="py-3 px-4 text-slate-700">{formatCurrency(row.vision_a.ca_net)}</td>
                        <td className="py-3 px-4 bg-slate-100/30 text-slate-600 font-mono">
                          {formatCurrency(row.vision_a.total_consultant_cost)}
                        </td>
                        <td className="py-3 px-4 bg-indigo-50/30 font-semibold text-indigo-900 font-mono">
                          {formatCurrency(row.vision_b.total_consultant_cost)}
                        </td>
                        <td className="py-3 px-4 font-mono font-medium text-amber-700">
                          {formatDelta(row.variance.total_cost_delta)}
                        </td>
                        <td className="py-3 px-4 bg-slate-100/30 text-slate-600 font-mono">
                          {formatCurrency(row.vision_a.gross_margin)} ({row.vision_a.margin_pct}%)
                        </td>
                        <td className="py-3 px-4 bg-indigo-50/30 font-semibold text-indigo-900 font-mono">
                          {formatCurrency(row.vision_b.gross_margin)} ({row.vision_b.margin_pct}%)
                        </td>
                        <td className="py-3 px-4 font-mono font-semibold text-slate-700">
                          {formatDelta(row.variance.gross_margin_delta)}
                        </td>
                        <td className="py-3 px-4 text-slate-600 max-w-xs truncate" title={row.variance.explanation}>
                          {row.variance.explanation}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {/* Ligne de Totaux Consolidés */}
                  <tfoot>
                    <tr className="bg-slate-100/80 font-bold border-t-2 border-slate-300 text-slate-900">
                      <td className="py-3 px-4">TOTAL {selectedYear}</td>
                      <td className="py-3 px-4">{formatCurrency(annualA?.ca_net)}</td>
                      <td className="py-3 px-4 font-mono">{formatCurrency(annualA?.total_consultant_cost)}</td>
                      <td className="py-3 px-4 font-mono text-indigo-900">{formatCurrency(annualB?.total_consultant_cost)}</td>
                      <td className="py-3 px-4 font-mono text-amber-700">{formatDelta(annualVar?.total_cost_delta)}</td>
                      <td className="py-3 px-4 font-mono">{formatCurrency(annualA?.gross_margin)} ({annualA?.margin_pct}%)</td>
                      <td className="py-3 px-4 font-mono text-indigo-900">{formatCurrency(annualB?.gross_margin)} ({annualB?.margin_pct}%)</td>
                      <td className="py-3 px-4 font-mono">{formatDelta(annualVar?.gross_margin_delta)}</td>
                      <td className="py-3 px-4 text-xs font-normal text-slate-500">Somme stricte des 12 mois</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* TAB 2 : Détail par Collaborateur */}
          {activeTab === "collaborators" && (
            <div className="rounded-xl border border-slate-200 bg-white shadow-xs overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-slate-50/80 border-b border-slate-200 text-slate-500 font-semibold uppercase tracking-wider">
                      <th className="py-3 px-4">Collaborateur</th>
                      <th className="py-3 px-4">Contrat</th>
                      <th className="py-3 px-4">Statut Opérationnel</th>
                      <th className="py-3 px-4">Salaire Annuel</th>
                      <th className="py-3 px-4 text-slate-600 bg-slate-100/50">Coût Actuel</th>
                      <th className="py-3 px-4 text-indigo-900 bg-indigo-50/50">Coût Enrichi</th>
                      <th className="py-3 px-4">Écart Coût</th>
                      <th className="py-3 px-4 text-slate-600 bg-slate-100/50">Marge Actuelle</th>
                      <th className="py-3 px-4 text-indigo-900 bg-indigo-50/50">Marge Enrichie</th>
                      <th className="py-3 px-4">Justification de l'Écart</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.collaborator_rows.map((row) => (
                      <tr key={row.collaborator_id} className="hover:bg-slate-50/50 transition">
                        <td className="py-3 px-4">
                          <div className="font-semibold text-slate-900">{row.collaborator_name}</div>
                          <div className="text-slate-400 text-2xs">{row.collaborator_email}</div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-mono text-2xs bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
                            {row.contract_kind}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`text-2xs px-2 py-0.5 rounded-full font-medium ${
                              row.operational_status === "EN_MISSION"
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                : row.operational_status === "INTERCONTRAT_SALARIE"
                                ? "bg-amber-50 text-amber-700 border border-amber-200"
                                : "bg-slate-50 text-slate-600 border border-slate-200"
                            }`}
                          >
                            {row.operational_status_label}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-mono text-slate-700">
                          {row.annual_salary ? formatCurrency(row.annual_salary) : "—"}
                        </td>
                        <td className="py-3 px-4 bg-slate-100/30 text-slate-600 font-mono">
                          {formatCurrency(row.cost_a)}
                        </td>
                        <td className="py-3 px-4 bg-indigo-50/30 font-semibold text-indigo-900 font-mono">
                          {formatCurrency(row.cost_b)}
                        </td>
                        <td className="py-3 px-4 font-mono font-medium text-amber-700">
                          {formatDelta(row.cost_delta)}
                        </td>
                        <td className="py-3 px-4 bg-slate-100/30 text-slate-600 font-mono">
                          {formatCurrency(row.margin_a)}
                        </td>
                        <td className="py-3 px-4 bg-indigo-50/30 font-semibold text-indigo-900 font-mono">
                          {formatCurrency(row.margin_b)}
                        </td>
                        <td className="py-3 px-4 text-slate-600 max-w-sm truncate" title={row.explanation}>
                          {row.explanation}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
